import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

/**
 * ConectaTuAgente — autoservicio de llaves personales del gateway ("trae el tuyo").
 * compact=true: wizard de 3 pasos para Claude/Cursor (sin tabla ni feed).
 * compact=false: UI experta (tabla de llaves, JSON, actividad, revocar/renombrar).
 */
const GATEWAY = axios.create({
  baseURL: import.meta.env.VITE_AGENT_GATEWAY_URL || 'https://api-beta-v2.flowexpay.com/agent-gateway',
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});
GATEWAY.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const MCP_URL = (import.meta.env.VITE_AGENT_GATEWAY_URL || 'https://api-beta-v2.flowexpay.com/agent-gateway') + '/mcp';

const snippet = (key) => JSON.stringify(
  { mcpServers: { flowexpay: { type: 'http', url: MCP_URL, headers: { 'X-Agent-Key': key } } } },
  null,
  2
);

const fmtF = (d) => (d ? new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function ConectaTuAgente({ compact = false }) {
  const [keys, setKeys] = useState(null); // null = no disponible/cargando
  const [label, setLabel] = useState('');
  const [creando, setCreando] = useState(false);
  const [nueva, setNueva] = useState(null); // { key, prefix, label } — visible UNA vez
  const [copiado, setCopiado] = useState('');
  const [err, setErr] = useState('');
  const [verTexto, setVerTexto] = useState(false);
  const [via, setVia] = useState('emparejar');
  const [pairing, setPairing] = useState(null);
  const [emparejando, setEmparejando] = useState(false);
  const [aprobando, setAprobando] = useState(false);

  // Piso 2: actividad de agentes via gateway (solo UI experta).
  const [actividad, setActividad] = useState([]);

  const cargar = useCallback(() => {
    GATEWAY.get('/agent/v1/me/keys')
      .then((r) => setKeys(r.data.keys || []))
      .catch(() => setKeys(null));
    if (compact) {
      setActividad([]);
      return;
    }
    GATEWAY.get('/agent/v1/me/activity', { params: { limit: 20 } })
      // Solo actividad de AGENTES: lo que hace esta misma pestaña (agentId 'bearer',
      // sin identificarse como agente) no es "lo que hicieron tus agentes".
      .then((r) => setActividad((r.data.entries || []).filter((e) => e.agentId && e.agentId !== 'bearer').slice(0, 12)))
      .catch(() => setActividad([]));
  }, [compact]);
  useEffect(() => { cargar(); }, [cargar]);

  const copiar = async (texto, tag) => {
    try { await navigator.clipboard.writeText(texto); setCopiado(tag); setTimeout(() => setCopiado(''), 2000); } catch {}
  };

  const empezarEmparejar = async () => {
    setEmparejando(true); setErr('');
    try {
      const { data } = await GATEWAY.post('/agent/v1/me/pairing', { label: label.trim() || 'Mi agente' });
      setPairing({
        user_code: data.user_code,
        device_code: data.device_code,
        verification_uri: data.verification_uri,
        expiresIn: data.expiresIn,
        interval: data.interval,
        status: 'pending',
      });
    } catch (e) {
      setErr(e?.response?.data?.message || 'No se pudo empezar el emparejamiento. Intenta de nuevo.');
    } finally { setEmparejando(false); }
  };

  const aprobarEmparejar = async () => {
    if (!pairing || !pairing.user_code) return;
    setAprobando(true); setErr('');
    try {
      const { data } = await GATEWAY.post(`/agent/v1/me/pairing/${encodeURIComponent(pairing.user_code)}/approve`);
      setPairing((p) => ({ ...p, status: data.status || 'approved', prefix: data.prefix }));
      cargar();
    } catch (e) {
      setErr(e?.response?.data?.message || 'No se pudo aprobar. Intenta de nuevo.');
    } finally { setAprobando(false); }
  };

  const crear = async () => {
    setCreando(true); setErr('');
    try {
      const { data } = await GATEWAY.post('/agent/v1/me/keys', { label: label.trim() || 'Mi agente' });
      setNueva(data); setLabel(''); setVerTexto(false);
      cargar();
    } catch (e) {
      setErr(e?.response?.data?.message || 'No se pudo crear la llave. Intenta de nuevo.');
    } finally { setCreando(false); }
  };

  const revocar = async (id) => {
    if (!window.confirm('¿Revocar esta llave? Tu IA conectada dejará de tener acceso al instante.')) return;
    try { await GATEWAY.post(`/agent/v1/me/keys/${id}/revoke`); cargar(); } catch {}
  };

  // La IA puede pausarse sola (freno de mano); reactivarla es SOLO del dueño.
  const reactivar = async (id) => {
    try { await GATEWAY.post(`/agent/v1/me/keys/${id}/resume`); cargar(); } catch {}
  };

  // Renombrar la llave = renombrar al agente en "Tus IAs conectadas" (toma el
  // nombre nuevo en su siguiente llamada al gateway).
  const renombrar = async (k) => {
    const nuevo = window.prompt(`Nuevo nombre para tu agente (hoy: ${k.label})`, k.label);
    if (nuevo == null || !nuevo.trim() || nuevo.trim() === k.label) return;
    try { await GATEWAY.post(`/agent/v1/me/keys/${k.id}/rename`, { label: nuevo.trim() }); cargar(); } catch {}
  };

  if (compact) {
    return (
      <div>
        <p className="text-sm text-gray-500">No puede sacar plata sin tu confirmación.</p>

        {via === 'emparejar' && (
          <div className="mt-3">
            <p className="text-sm font-semibold text-gray-900">Emparejar</p>
            {!pairing && (
              <div className="mt-2 flex gap-2 flex-wrap">
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') empezarEmparejar(); }}
                  placeholder="ej. Claude"
                  maxLength={40}
                  className="flex-1 min-w-[140px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  disabled={emparejando}
                  onClick={empezarEmparejar}
                  className="text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white disabled:opacity-50"
                >
                  {emparejando ? 'Creando código…' : 'Emparejar'}
                </button>
              </div>
            )}
            {pairing && (pairing.status === 'pending') && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Tu código</p>
                <p className="mt-1 text-3xl font-mono font-semibold tracking-[0.2em] text-[#0a2540] text-center">
                  {pairing.user_code}
                </p>
                <p className="mt-3 text-sm text-gray-700">Aprueba cuando Claude pida el código</p>
                {pairing.verification_uri && (
                  <p className="mt-1 text-xs text-gray-500 break-all">{pairing.verification_uri}</p>
                )}
                <button
                  type="button"
                  disabled={aprobando}
                  onClick={aprobarEmparejar}
                  className="mt-3 w-full text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white font-medium disabled:opacity-50"
                >
                  {aprobando ? 'Aprobando…' : 'Aprobar'}
                </button>
                <button
                  type="button"
                  onClick={() => copiar(pairing.device_code, 'device')}
                  className="mt-2 text-xs text-gray-500 underline"
                >
                  {copiado === 'device' ? 'Copiado' : 'Copiar device_code para que Claude pida el token'}
                </button>
              </div>
            )}
            {pairing && (pairing.status === 'approved' || pairing.status === 'consumed') && (
              <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-4">
                <p className="text-sm font-semibold text-green-900">Listo. Claude ya puede pedir el token.</p>
                <p className="text-sm text-green-800 mt-1">Execute sigue cerrado: no puede mover tu plata.</p>
                {pairing.prefix && (
                  <p className="mt-1 text-xs font-mono text-green-800">{pairing.prefix}…</p>
                )}
              </div>
            )}
            {pairing && pairing.status === 'expired' && (
              <div className="mt-3">
                <p className="text-sm text-gray-700">Este código ya expiró. Empieza de nuevo.</p>
                <button
                  type="button"
                  onClick={() => { setPairing(null); setErr(''); }}
                  className="mt-2 text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white"
                >
                  Emparejar de nuevo
                </button>
              </div>
            )}
          </div>
        )}

        {via === 'llave' && (
          <>
            {!nueva && (
              <div className="mt-3">
                <p className="text-sm font-semibold text-gray-900">Paso 1. Ponle un nombre</p>
                <div className="mt-2 flex gap-2 flex-wrap">
                  <input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') crear(); }}
                    placeholder="ej. Salomón"
                    maxLength={40}
                    className="flex-1 min-w-[140px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    disabled={creando}
                    onClick={crear}
                    className="text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white disabled:opacity-50"
                  >
                    {creando ? 'Conectando…' : 'Conectar'}
                  </button>
                </div>
              </div>
            )}

            {nueva && (
              <>
                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
                  <p className="text-sm font-semibold text-amber-900">Paso 2</p>
                  <p className="text-xs text-amber-800 mt-1">Te la mostramos una sola vez.</p>
                  <button
                    type="button"
                    onClick={() => copiar(snippet(nueva.key), 'cfg')}
                    className="mt-3 w-full text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white font-medium"
                  >
                    {copiado === 'cfg' ? 'Copiada' : 'Copiar para pegar en Claude o Cursor'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setVerTexto((v) => !v)}
                    className="mt-2 text-xs text-amber-900 font-medium underline"
                  >
                    {verTexto ? 'Ocultar el texto' : 'Ver el texto'}
                  </button>
                  {verTexto && (
                    <pre className="mt-2 text-[11px] bg-white border border-amber-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{snippet(nueva.key)}</pre>
                  )}
                </div>
                <div className="mt-3">
                  <p className="text-sm font-semibold text-gray-900">Paso 3</p>
                  <p className="text-sm text-gray-600 mt-1">
                    En Claude o Cursor: Ajustes → conectores / MCP → pega lo copiado.
                  </p>
                </div>
              </>
            )}
          </>
        )}

        {err && <p className="mt-2 text-sm text-red-600">{err}</p>}

        <button
          type="button"
          onClick={() => {
            setVia((v) => v === 'emparejar' ? 'llave' : 'emparejar');
            setErr('');
          }}
          className="mt-4 text-xs text-gray-500 underline"
        >
          {via === 'emparejar' ? 'Ya tengo la llave / generar llave' : 'Emparejar con un código'}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8 bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-gray-900">Conecta tu propio agente</h2>
        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">Para expertos · beta</span>
      </div>
      <p className="text-sm text-gray-500 mt-1">
        ¿Ya usas tu propia IA (Claude, ChatGPT, Cursor)? Conéctala a FlowexPay con una llave personal:
        podrá consultar tu portafolio, las señales del mercado y el análisis de la casa, y simular
        operaciones. <span className="font-medium text-gray-700">Nunca podrá sacar tu plata</span>:
        ejecutar dinero siempre exigirá tu confirmación en la app.
      </p>

      {/* Llave recién creada: se muestra UNA vez */}
      {nueva && (
        <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">Tu llave nueva — guárdala ahora, no se volverá a mostrar</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-white border border-amber-200 rounded px-2 py-1.5 break-all flex-1 min-w-[200px]">{nueva.key}</code>
            <button onClick={() => copiar(nueva.key, 'key')} className="text-xs px-3 py-1.5 rounded-lg bg-[#2A59C6] text-white">
              {copiado === 'key' ? 'Copiada' : 'Copiar llave'}
            </button>
          </div>
          <p className="text-xs font-medium text-amber-900 mt-3">Configuración lista para pegar (Claude Desktop / clientes MCP):</p>
          <pre className="mt-1 text-[11px] bg-white border border-amber-200 rounded p-2 overflow-x-auto">{snippet(nueva.key)}</pre>
          <div className="mt-2 flex gap-2">
            <button onClick={() => copiar(snippet(nueva.key), 'cfg')} className="text-xs px-3 py-1.5 rounded-lg border border-amber-400 text-amber-900">
              {copiado === 'cfg' ? 'Copiada' : 'Copiar configuración'}
            </button>
            <button onClick={() => setNueva(null)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600">Ya la guardé</button>
          </div>
        </div>
      )}

      {/* Crear */}
      {keys !== null && !nueva && (
        <div className="mt-4 flex gap-2 flex-wrap">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Nombre de la llave (ej: Mi Claude)"
            maxLength={40}
            className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <button disabled={creando} onClick={crear} className="text-sm px-4 py-2 rounded-lg bg-[#2A59C6] text-white disabled:opacity-50">
            {creando ? 'Creando…' : 'Crear llave'}
          </button>
        </div>
      )}
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}

      {/* Lista */}
      {keys === null ? (
        <p className="mt-4 text-sm text-gray-400">La conexión de agentes propios no está disponible en este momento.</p>
      ) : keys.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3">Llave</th>
                <th className="py-2 pr-3">Nombre</th>
                <th className="py-2 pr-3">Creada</th>
                <th className="py-2 pr-3">Último uso</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-b border-gray-50">
                  <td className="py-2 pr-3 font-mono text-xs">{k.prefix}…</td>
                  <td className="py-2 pr-3">{k.label}</td>
                  <td className="py-2 pr-3 text-gray-500">{fmtF(k.createdAt)}</td>
                  <td className="py-2 pr-3 text-gray-500">{k.lastUsedAt ? fmtF(k.lastUsedAt) : 'Nunca'}</td>
                  <td className="py-2 pr-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        k.pausada
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : k.activa
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}
                      title={k.pausada && k.pauseMotivo ? `Se pausó: «${k.pauseMotivo}»` : undefined}
                    >
                      {k.pausada ? 'En pausa' : k.activa ? 'Activa' : 'Revocada'}
                    </span>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {k.activa && (
                      <>
                        {k.pausada && (
                          <button onClick={() => reactivar(k.id)} className="text-xs text-green-700 font-medium mr-3">Reactivar</button>
                        )}
                        <button onClick={() => renombrar(k)} className="text-xs text-[#2A59C6] font-medium mr-3">Renombrar</button>
                        <button onClick={() => revocar(k.id)} className="text-xs text-red-600 font-medium">Revocar</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-gray-400">
        Tu llave es personal: quien la tenga puede LEER tu información de FlowexPay, así que guárdala
        como una contraseña. Si sospechas que se filtró, revócala aquí — deja de funcionar al instante.
      </p>

      {/* ACTIVIDAD: qué ha hecho cada agente (transparencia total) */}
      {actividad.length > 0 && (
        <div className="mt-5 pt-4 border-t border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Lo último que hicieron tus agentes</h3>
          <div className="mt-2 space-y-1.5">
            {actividad.map((a) => (
              <div key={a.id} className="flex items-center gap-2 text-xs">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.ok === false ? 'bg-red-400' : 'bg-gray-300'}`} />
                <span className="text-gray-600 flex-1 min-w-0 truncate">{a.label || a.tool}</span>
                <span className="text-gray-400 shrink-0">
                  {a.ts ? new Date(a.ts).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
